/**
 * Get market information
 */
import type { Address } from "viem"
import {
	deriveMarketStatus,
	type ReadableClient,
	readEmergencyPausedAt,
	readMarketInfo,
	readPrices,
	readResolution,
} from "../../internal/contracts"
import { fromPriceScale, fromTimestamp, fromUsdcScale } from "../../internal/convert"
import type { BucketInfo, Market } from "../../types"

export interface GetMarketParams {
	address: Address
}

/**
 * Get complete market information
 *
 * @example
 * ```ts
 * import { getMarket } from "@breakout/sdk/actions/read"
 *
 * const market = await getMarket(publicClient, { address: "0x..." })
 * console.log(market.status) // { type: "open", closesIn: 3600000 }
 * ```
 */
export async function getMarket(client: ReadableClient, params: GetMarketParams): Promise<Market> {
	const { address } = params

	// Fetch all data in parallel
	const [info, prices, resolution, pausedAt] = await Promise.all([
		readMarketInfo(client, address),
		readPrices(client, address),
		readResolution(client, address),
		readEmergencyPausedAt(client, address),
	])

	// Build bucket info
	const buckets: BucketInfo[] = prices.map((price, index) => ({
		index,
		price: fromPriceScale(price),
	}))

	// Derive status
	const status = deriveMarketStatus(
		info.status,
		info.openTimeSec,
		info.cutoffTimeSec,
		resolution,
		pausedAt,
	)

	return {
		address,
		status,
		buckets,
		openTime: fromTimestamp(info.openTimeSec),
		cutoffTime: fromTimestamp(info.cutoffTimeSec),
		feeBps: Number(info.feeBps),
		liquidity: fromUsdcScale(info.liquidityParam),
	}
}
